import React from 'react';
import axios from 'axios';
import {
    Accordion,
    Button,
    Form,
    Icon,
    Label,
    Loader,
    Message,
    Popup,
    Table,
} from 'semantic-ui-react';
import { ToastContainer, toast } from 'react-toastify';

axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.withCredentials = true;

export default class TemplateCatalogOverview extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            actionLoading: false,
            error: false,
            errorText: '',
            searchText: '',
            catalog: [],
            attributes: [],
            attributeDefinitionsByTemplate: {},
            documentTypes: [],
            pendingAttributeByTemplate: {},
            expandedAddForTemplateId: null,
            showCreateAttributeForm: false,
            showCreateTemplateForm: false,
            newAttributeName: '',
            newAttributeDisplayName: '',
            newAttributeType: 'TEXT',
            newAttributeTranslatable: true,
            newTemplateName: '',
            newTemplateDocumentTypeId: '',
            activeIndex: -1,
        };
        this._fetchCatalogData = this._fetchCatalogData.bind(this);
        this._handleSearchChange = this._handleSearchChange.bind(this);
        this._handleAccordionClick = this._handleAccordionClick.bind(this);
        this._matchesSearch = this._matchesSearch.bind(this);
        this._toggleTemplateAddSection = this._toggleTemplateAddSection.bind(this);
        this._handlePendingAttributeChange = this._handlePendingAttributeChange.bind(this);
        this._handleAddMapping = this._handleAddMapping.bind(this);
        this._handleDeleteMapping = this._handleDeleteMapping.bind(this);
        this._extractErrorMessage = this._extractErrorMessage.bind(this);
        this._fetchTemplateAttributeDefinitions = this._fetchTemplateAttributeDefinitions.bind(this);
        this._getAttributeOrderNumber = this._getAttributeOrderNumber.bind(this);
        this._compareCatalogAttributes = this._compareCatalogAttributes.bind(this);
        this._getSortedAttributeMappings = this._getSortedAttributeMappings.bind(this);
        this._toggleCreateAttributeForm = this._toggleCreateAttributeForm.bind(this);
        this._toggleCreateTemplateForm = this._toggleCreateTemplateForm.bind(this);
        this._handleCatalogInputChange = this._handleCatalogInputChange.bind(this);
        this._createCatalogAttribute = this._createCatalogAttribute.bind(this);
        this._createCatalogTemplate = this._createCatalogTemplate.bind(this);
    }

    componentDidMount() {
        this._fetchCatalogData();
    }

    _fetchTemplateAttributeDefinitions(templateIds) {
        const uniqueTemplateIds = Array.from(new Set((templateIds || []).filter(templateId => !!templateId)));

        if (uniqueTemplateIds.length === 0) {
            return Promise.resolve({});
        }

        return Promise.all(uniqueTemplateIds.map(templateId => axios.get(`/template-configuration/attributes/${templateId}`)
            .then(response => ({
                templateId,
                attributes: Array.isArray(response.data) ? response.data : [],
            }))
            .catch(() => ({
                templateId,
                attributes: [],
            }))))
            .then(results => results.reduce((definitionsByTemplate, result) => ({
                ...definitionsByTemplate,
                [result.templateId]: result.attributes,
            }), {}));
    }

    _getAttributeOrderNumber(attribute) {
        const normalizedOrderNumber = Number(attribute && attribute.orderNumber);
        return Number.isFinite(normalizedOrderNumber)
            ? normalizedOrderNumber
            : Number.MAX_SAFE_INTEGER;
    }

    _compareCatalogAttributes(leftAttribute, rightAttribute) {
        const leftOrderNumber = this._getAttributeOrderNumber(leftAttribute);
        const rightOrderNumber = this._getAttributeOrderNumber(rightAttribute);

        if (leftOrderNumber !== rightOrderNumber) {
            return leftOrderNumber - rightOrderNumber;
        }

        const leftName = (leftAttribute.displayName || leftAttribute.name || '').toLowerCase();
        const rightName = (rightAttribute.displayName || rightAttribute.name || '').toLowerCase();
        const nameCompare = leftName.localeCompare(rightName, undefined, { sensitivity: 'base', numeric: true });

        if (nameCompare !== 0) {
            return nameCompare;
        }

        return Number(leftAttribute.id || Number.MAX_SAFE_INTEGER) - Number(rightAttribute.id || Number.MAX_SAFE_INTEGER);
    }

    _getSortedAttributeMappings(templateId, attributeMappings) {
        const templateDefinitions = this.state.attributeDefinitionsByTemplate[templateId] || [];
        const templateDefinitionsById = templateDefinitions.reduce((accumulator, attribute) => ({
            ...accumulator,
            [attribute.id]: attribute,
        }), {});

        return [...attributeMappings].sort((leftMapping, rightMapping) => {
            const leftAttribute = {
                ...(templateDefinitionsById[(leftMapping.attribute || {}).id] || {}),
                ...(leftMapping.attribute || {}),
            };
            const rightAttribute = {
                ...(templateDefinitionsById[(rightMapping.attribute || {}).id] || {}),
                ...(rightMapping.attribute || {}),
            };

            return this._compareCatalogAttributes(leftAttribute, rightAttribute);
        });
    }

    _fetchCatalogData() {
        this.setState({ loading: true, error: false, errorText: '' });
        Promise.all([
            axios.get('/template-configuration/catalog/template-attribute-mapping'),
            axios.get('/template-configuration/catalog/attributes'),
            axios.get('/template-configuration/catalog/document-types'),
        ])
            .then(([mappingRes, attributesRes, documentTypesRes]) => {
                const catalog = Array.isArray(mappingRes.data) ? mappingRes.data : [];
                const attributes = Array.isArray(attributesRes.data) ? attributesRes.data : [];
                const documentTypes = Array.isArray(documentTypesRes.data) ? documentTypesRes.data : [];
                return this._fetchTemplateAttributeDefinitions(catalog.map(item => item && item.template && item.template.id))
                    .then(attributeDefinitionsByTemplate => ({
                        catalog,
                        attributes,
                        documentTypes,
                        attributeDefinitionsByTemplate,
                    }));
            })
            .then(({
                catalog,
                attributes,
                documentTypes,
                attributeDefinitionsByTemplate,
            }) => {
                this.setState({
                    loading: false,
                    catalog,
                    attributes,
                    documentTypes,
                    attributeDefinitionsByTemplate,
                    activeIndex: -1,
                });
            })
            .catch(() => this.setState({
                loading: false,
                error: true,
                errorText: 'Greška pri učitavanju kataloga dokumenata i atributa.',
            }));
    }

    _extractErrorMessage(error, fallbackMessage) {
        const responseData = error && error.response ? error.response.data : null;
        if (responseData && responseData.message) {
            const backendMessage = responseData.message;
            if (backendMessage === 'Cannot unlink attribute because configuration values already reference this mapping') {
                return 'Nije moguće ukloniti vezu atributa jer je već korištena u konfiguraciji.';
            }
            const duplicateAttributePrefix = 'Attribute with this name already exists:';
            if (backendMessage.indexOf(duplicateAttributePrefix) === 0) {
                const attributeName = backendMessage.substring(duplicateAttributePrefix.length).trim();
                return attributeName ? `Atribut ${attributeName} već postoji` : 'Atribut već postoji';
            }
            const duplicateTemplatePrefix = 'Template with this name already exists:';
            if (backendMessage.indexOf(duplicateTemplatePrefix) === 0) {
                const templateName = backendMessage.substring(duplicateTemplatePrefix.length).trim();
                return templateName ? `Dokument ${templateName} već postoji` : 'Dokument već postoji';
            }
            if (backendMessage === 'Template for this document type already exists') {
                return 'Dokument za odabrani tip već postoji';
            }
            return backendMessage;
        }
        if (typeof responseData === 'string' && responseData.trim()) {
            return responseData;
        }
        return fallbackMessage;
    }

    _toggleTemplateAddSection(templateId) {
        this.setState(prevState => ({
            expandedAddForTemplateId: prevState.expandedAddForTemplateId === templateId ? null : templateId,
        }));
    }

    _handlePendingAttributeChange(templateId, value) {
        this.setState(prevState => ({
            pendingAttributeByTemplate: {
                ...prevState.pendingAttributeByTemplate,
                [templateId]: value,
            },
        }));
    }

    _handleAddMapping(templateId) {
        const { pendingAttributeByTemplate } = this.state;
        const selectedAttributeId = pendingAttributeByTemplate[templateId];

        if (!templateId || !selectedAttributeId) {
            toast('Odaberite dokument i atribut prije povezivanja.');
            return;
        }

        this.setState({ actionLoading: true });
        axios.post('/template-configuration/catalog/template-attribute-mapping', {
            templateId,
            attributeId: selectedAttributeId,
        })
            .then(() => {
                this.setState(prevState => ({
                    actionLoading: false,
                    pendingAttributeByTemplate: {
                        ...prevState.pendingAttributeByTemplate,
                        [templateId]: '',
                    },
                }));
                toast('Atribut je uspješno povezan sa dokumentom.');
                this._fetchCatalogData();
            })
            .catch(() => {
                this.setState({
                    actionLoading: false,
                });
                toast('Povezivanje atributa nije uspjelo.');
            });
    }

    _handleDeleteMapping(mappingId) {
        this.setState({ actionLoading: true });
        axios.delete(`/template-configuration/catalog/template-attribute-mapping/${mappingId}`)
            .then(() => {
                this.setState({
                    actionLoading: false,
                });
                toast('Veza atributa je uspješno uklonjena.');
                this._fetchCatalogData();
            })
            .catch(error => {
                const errorMessage = this._extractErrorMessage(error, 'Uklanjanje veze nije uspjelo.');
                this.setState({
                    actionLoading: false,
                });
                if (errorMessage === 'Cannot unlink attribute because configuration values already reference this mapping') {
                    toast('Nije moguće ukloniti vezu atributa jer je već korištena u nekoj od postojećih konfiguracija.');
                } else {
                    toast('Uklanjanje veze trenuntno nije moguće');
                }
            });
    }

    _toggleCreateAttributeForm() {
        this.setState(prevState => ({
            showCreateAttributeForm: !prevState.showCreateAttributeForm,
            showCreateTemplateForm: false,
        }));
    }

    _toggleCreateTemplateForm() {
        this.setState(prevState => ({
            showCreateAttributeForm: false,
            showCreateTemplateForm: !prevState.showCreateTemplateForm,
        }));
    }

    _handleCatalogInputChange(event, { name, value }) {
        this.setState({ [name]: value });
    }

    _createCatalogAttribute() {
        const {
            newAttributeName,
            newAttributeDisplayName,
            newAttributeType,
            newAttributeTranslatable,
        } = this.state;
        if (!newAttributeName.trim() || !newAttributeDisplayName.trim()) {
            toast('Niste unijeli sva obavezna polja!');
            return;
        }

        const payload = {
            name: newAttributeName.trim(),
            displayName: newAttributeDisplayName.trim(),
            attributeType: newAttributeType,
            translatable: newAttributeTranslatable,
        };

        this.setState({ actionLoading: true });
        axios.post('/template-configuration/catalog/attributes', payload)
            .catch(error => {
                // Backward compatibility: older backend exposes lookup POST on /attributes only.
                if (error && error.response && error.response.status === 405) {
                    return axios.post('/attributes', payload);
                }
                throw error;
            })
            .then(() => {
                this.setState({
                    actionLoading: false,
                    newAttributeName: '',
                    newAttributeDisplayName: '',
                    newAttributeType: 'TEXT',
                    newAttributeTranslatable: true,
                    showCreateAttributeForm: false,
                });
                toast('Novi atribut je uspješno kreiran.');
                this._fetchCatalogData();
            })
            .catch(() => {
                this.setState({
                    actionLoading: false,
                });
                toast('Kreiranje atributa nije uspjelo.');
            });
    }

    _createCatalogTemplate() {
        const { newTemplateName, newTemplateDocumentTypeId } = this.state;
        if (!newTemplateName.trim() || !newTemplateDocumentTypeId) {
            toast('Niste unijeli sva obavezna polja!');
            return;
        }

        const catalogPayload = {
            name: newTemplateName.trim(),
            documentTypeId: newTemplateDocumentTypeId,
        };
        const legacyPayload = {
            name: newTemplateName.trim(),
            documentType: { id: newTemplateDocumentTypeId },
        };

        this.setState({ actionLoading: true });
        axios.post('/template-configuration/catalog/templates', catalogPayload)
            .catch(error => {
                // Backward compatibility: older backend exposes lookup POST on /document-templates only.
                if (error && error.response && error.response.status === 405) {
                    return axios.post('/document-templates', legacyPayload);
                }
                throw error;
            })
            .then(() => {
                this.setState({
                    actionLoading: false,
                    newTemplateName: '',
                    newTemplateDocumentTypeId: '',
                    showCreateTemplateForm: false,
                });
                toast('Novi dokument je uspješno kreiran.');
                this._fetchCatalogData();
            })
            .catch(() => {
                this.setState({
                    actionLoading: false,
                });
                toast('Kreiranje dokumenta nije uspjelo.');
            });
    }

    _handleSearchChange(event, { value }) {
        this.setState({ searchText: value, activeIndex: -1 });
    }

    _handleAccordionClick(index) {
        this.setState(prevState => ({
            activeIndex: prevState.activeIndex === index ? -1 : index,
        }));
    }

    _matchesSearch(template, searchText) {
        if (!searchText) return true;

        const templateName = (template.template && template.template.name ? template.template.name : '').toLowerCase();
        const templateDisplayName = (template.template && template.template.displayName ? template.template.displayName : '').toLowerCase();
        const mappingItems = Array.isArray(template.attributeMappings) ? template.attributeMappings : [];

        if (templateName.includes(searchText) || templateDisplayName.includes(searchText)) {
            return true;
        }

        return mappingItems.some(item => {
            const attribute = item.attribute || {};
            const attributeName = (attribute.name || '').toLowerCase();
            const attributeDisplayName = (attribute.displayName || '').toLowerCase();
            return attributeName.includes(searchText) || attributeDisplayName.includes(searchText);
        });
    }

    render() {
        const {
            loading,
            actionLoading,
            error,
            errorText,
            searchText,
            catalog,
            attributes,
            documentTypes,
            pendingAttributeByTemplate,
            expandedAddForTemplateId,
            showCreateAttributeForm,
            showCreateTemplateForm,
            newAttributeName,
            newAttributeDisplayName,
            newAttributeType,
            newAttributeTranslatable,
            newTemplateName,
            newTemplateDocumentTypeId,
            activeIndex,
        } = this.state;
        const attributeTypeOptions = [
            { key: 'TEXT', value: 'TEXT', text: 'Tekst' },
            { key: 'BOOLEAN', value: 'BOOLEAN', text: 'Boolean (Da/Ne)' },
        ];
        const translatableOptions = [
            { key: 'yes', value: true, text: 'Da' },
            { key: 'no', value: false, text: 'Ne' },
        ];
        const normalizedSearch = searchText.trim().toLowerCase();
        const filteredCatalog = catalog.filter(template => this._matchesSearch(template, normalizedSearch));
        const documentTypeOptions = documentTypes.map(type => {
            const typeName = type.name || '-';
            const typeKey = type.key || '[key nije dostupan]';
            return {
                key: type.id,
                value: type.id,
                text: `${type.id}: ${typeName} | ${typeKey}`,
                content: (
                    <span>
                        <span>{`${type.id}: ${typeName}`}</span>
                        <span className="template-catalog-muted-meta">{` | ${typeKey}`}</span>
                    </span>
                ),
            };
        });

        return (
            <div>
                <div>
                    <Form>
                        <Form.Group widths="equal">
                            <Form.Input
                                placeholder="Pretraga po nazivu dokumenta ili atributa"
                                value={searchText}
                                onChange={this._handleSearchChange}
                                icon="search"
                            />
                            <Button type="button" onClick={this._fetchCatalogData} icon labelPosition="left" basic color="blue">
                                <Icon name="refresh" />
                                Osvježi
                            </Button>
                        </Form.Group>

                        <Form.Group>
                            <Button
                                type="button"
                                basic
                                color="purple"
                                icon
                                labelPosition="left"
                                size="small"
                                onClick={this._toggleCreateAttributeForm}
                                disabled={actionLoading}
                            >
                                <Icon name={showCreateAttributeForm ? 'angle up' : 'plus'} />
                                Novi atribut
                            </Button>

                            <Button
                                type="button"
                                basic
                                color="teal"
                                icon
                                labelPosition="left"
                                size="small"
                                onClick={this._toggleCreateTemplateForm}
                                disabled={actionLoading}
                            >
                                <Icon name={showCreateTemplateForm ? 'angle up' : 'plus'} />
                                Novi dokument
                            </Button>
                        </Form.Group>

                        {showCreateAttributeForm && (
                            <div>
                                <Form.Group widths="three">
                                    <Form.Field>
                                        <label htmlFor="newAttributeDisplayNameInput">Naziv atributa</label>
                                        <Form.Input
                                            id="newAttributeDisplayNameInput"
                                            name="newAttributeDisplayName"
                                            placeholder="Naziv atributa"
                                            value={newAttributeDisplayName}
                                            onChange={this._handleCatalogInputChange}
                                            disabled={actionLoading}
                                        />
                                    </Form.Field>
                                    <Form.Field>
                                        <label htmlFor="newAttributeNameInput">_key</label>
                                        <Form.Input
                                            id="newAttributeNameInput"
                                            name="newAttributeName"
                                            placeholder="key (npr: signatoryLeft)"
                                            value={newAttributeName}
                                            onChange={this._handleCatalogInputChange}
                                            disabled={actionLoading}
                                        />
                                    </Form.Field>
                                    <Form.Field>
                                        <label htmlFor="newAttributeTypeSelect">Tip atributa</label>
                                        <Form.Select
                                            id="newAttributeTypeSelect"
                                            name="newAttributeType"
                                            options={attributeTypeOptions}
                                            value={newAttributeType}
                                            onChange={this._handleCatalogInputChange}
                                            disabled={actionLoading}
                                            fluid
                                        />
                                    </Form.Field>
                                </Form.Group>

                                <Form.Group widths="three">
                                    <Form.Field>
                                        <label htmlFor="newAttributeTranslatableSelect">Prevodi se</label>
                                        <Form.Select
                                            id="newAttributeTranslatableSelect"
                                            name="newAttributeTranslatable"
                                            options={translatableOptions}
                                            value={newAttributeTranslatable}
                                            onChange={this._handleCatalogInputChange}
                                            disabled={actionLoading}
                                            fluid
                                        />
                                    </Form.Field>
                                    <Form.Field>
                                        <label htmlFor="createAttributeButton">Akcija</label>
                                        <Button
                                            id="createAttributeButton"
                                            type="button"
                                            color="purple"
                                            icon
                                            labelPosition="left"
                                            loading={actionLoading}
                                            onClick={this._createCatalogAttribute}
                                            disabled={actionLoading}
                                            fluid
                                        >
                                            <Icon name="save" />
                                            Kreiraj atribut
                                        </Button>
                                    </Form.Field>
                                    <Form.Field />
                                </Form.Group>

                                <div>
                                    <strong>Tip atributa</strong> određuje način unosa vrijednosti (npr. tekst ili Da/Ne) <strong>Prevodi se</strong> određuje da li atribut ima i englesku vrijednost.
                                </div>
                            </div>
                        )}

                        {showCreateTemplateForm && (
                            <div>
                                <Form.Group widths="three">
                                    <Form.Field>
                                        <label htmlFor="newTemplateDocumentTypeSelect">Tip dokumenta (LKP_document_type)</label>
                                        <Form.Select
                                            id="newTemplateDocumentTypeSelect"
                                            name="newTemplateDocumentTypeId"
                                            placeholder="Odaberite tip dokumenta"
                                            options={documentTypeOptions}
                                            value={newTemplateDocumentTypeId}
                                            onChange={this._handleCatalogInputChange}
                                            disabled={actionLoading}
                                            search
                                            fluid
                                        />
                                    </Form.Field>
                                    <Form.Field>
                                        <label htmlFor="newTemplateNameInput">_key</label>
                                        <Form.Input
                                            id="newTemplateNameInput"
                                            name="newTemplateName"
                                            placeholder="key (npr: transcript_of_records)"
                                            value={newTemplateName}
                                            onChange={this._handleCatalogInputChange}
                                            disabled={actionLoading}
                                        />
                                    </Form.Field>
                                    <Form.Field>
                                        <label htmlFor="createTemplateButton">Akcija</label>
                                        <Button
                                            id="createTemplateButton"
                                            type="button"
                                            color="teal"
                                            icon
                                            labelPosition="left"
                                            loading={actionLoading}
                                            onClick={this._createCatalogTemplate}
                                            disabled={actionLoading}
                                            fluid
                                        >
                                            <Icon name="save" />
                                            Kreiraj dokument
                                        </Button>
                                    </Form.Field>
                                </Form.Group>

                                <div>
                                    Prikazani su samo slobodni tipovi dokumenata.
                                </div>
                            </div>
                        )}
                    </Form>
                </div>

                <div>
                    {loading && <Loader active inline="centered" />}

                    {error && <Message negative>{errorText}</Message>}

                    {!loading && !error && (
                        <div>

                            {filteredCatalog.length === 0 ? (
                                <Message warning>Nema rezultata za zadanu pretragu.</Message>
                            ) : (
                                <Accordion fluid styled>
                                    {filteredCatalog.map((template, index) => {
                                        const templateInfo = template.template || {};
                                        const attributeMappings = Array.isArray(template.attributeMappings) ? template.attributeMappings : [];
                                        const templateId = templateInfo.id;
                                        const usedAttributeIds = new Set(attributeMappings.map(mapping => (mapping.attribute || {}).id));
                                        const availableAttributes = attributes
                                            .filter(attribute => !usedAttributeIds.has(attribute.id))
                                            .sort(this._compareCatalogAttributes);
                                        const sortedAttributeMappings = this._getSortedAttributeMappings(templateId, attributeMappings);
                                        const availableAttributeOptions = availableAttributes.map(attribute => ({
                                            key: attribute.id,
                                            value: attribute.id,
                                            text: `${attribute.id}: ${attribute.displayName || '-'} | ${attribute.name || '-'} | ${attribute.attributeType || 'TEXT'} | ${attribute.translatable === false ? 'Ne' : 'Da'}`,
                                            content: (
                                                <span>
                                                    <span>{`${attribute.id}: ${attribute.displayName || '-'}`}</span>
                                                    <span className="template-catalog-muted-meta">{` | ${attribute.name || '-'} | ${attribute.attributeType || 'TEXT'} | prevodi se: ${attribute.translatable === false ? 'Ne' : 'Da'}`}</span>
                                                </span>
                                            ),
                                        }));
                                        const selectedPendingAttribute = pendingAttributeByTemplate[templateId] || '';
                                        const addSectionOpen = expandedAddForTemplateId === templateId;
                                        const isActive = activeIndex === index;
                                        const attributeCount = attributeMappings.length;
                                        const lastTwoDigits = attributeCount % 100;
                                        const lastDigit = attributeCount % 10;
                                        const isSingularAttributeCount = lastDigit === 1 && (lastTwoDigits < 11 || lastTwoDigits > 14);
                                        const attributeCountLabel = isSingularAttributeCount
                                            ? `${attributeCount} atribut`
                                            : `${attributeCount} atributa`;

                                        return (
                                            <React.Fragment key={templateInfo.id || index}>
                                                <Accordion.Title active={isActive} index={index} onClick={() => this._handleAccordionClick(index)}>
                                                    <Icon name="dropdown" color="teal" />
                                                    {templateInfo.displayName || templateInfo.name || 'Nepoznat dokument'}
                                                    {templateInfo.id ? (
                                                        <Label basic color="teal">
                                                            ID: {templateInfo.id}
                                                        </Label>
                                                    ) : null}
                                                    {templateInfo.name ? (
                                                        <Label basic color="teal">
                                                            {templateInfo.name}
                                                        </Label>
                                                    ) : null}

                                                    {!isActive && (
                                                        <span className="right floated">
                                                            <Label color="purple">
                                                                {attributeCountLabel}
                                                            </Label>
                                                        </span>
                                                    )}
                                                </Accordion.Title>
                                                <Accordion.Content active={isActive}>
                                                    <div>
                                                        {availableAttributes.length === 0 ? (
                                                            <Message info>
                                                                Svi dostupni atributi su vec povezani za ovaj dokument.
                                                            </Message>
                                                        ) : (
                                                            <div>
                                                                {addSectionOpen && (
                                                                    <Form>
                                                                        <Form.Group widths="three">
                                                                            <Form.Field>
                                                                                <label htmlFor={`templateAddAttribute${templateId}`}>Atribut</label>
                                                                                <Form.Select
                                                                                    id={`templateAddAttribute${templateId}`}
                                                                                    placeholder="Odaberite atribut za povezivanje"
                                                                                    options={availableAttributeOptions}
                                                                                    value={selectedPendingAttribute}
                                                                                    onChange={(event, { value }) => this._handlePendingAttributeChange(templateId, value)}
                                                                                    disabled={actionLoading}
                                                                                    search
                                                                                    fluid
                                                                                />
                                                                            </Form.Field>
                                                                            <Form.Field>
                                                                                <label htmlFor={`templateAddAttributeSubmit${templateId}`}>Akcija</label>
                                                                                <Button
                                                                                    id={`templateAddAttributeSubmit${templateId}`}
                                                                                    type="button"
                                                                                    color="green"
                                                                                    icon
                                                                                    labelPosition="left"
                                                                                    loading={actionLoading}
                                                                                    disabled={actionLoading || !selectedPendingAttribute}
                                                                                    onClick={() => this._handleAddMapping(templateId)}
                                                                                    fluid
                                                                                >
                                                                                    <Icon name="linkify" />
                                                                                    Potvrdi vezu
                                                                                </Button>
                                                                            </Form.Field>
                                                                            <Form.Field />
                                                                        </Form.Group>
                                                                    </Form>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div>
                                                        {sortedAttributeMappings.length === 0 ? (
                                                            <Message warning>Nema povezanih atributa.</Message>
                                                        ) : (
                                                            <Table celled selectable compact stackable>
                                                                <Table.Header>
                                                                    <Table.Row>
                                                                        <Table.HeaderCell width={1}>ID</Table.HeaderCell>
                                                                        <Table.HeaderCell width={4}>Naziv</Table.HeaderCell>
                                                                        <Table.HeaderCell width={4}>Key</Table.HeaderCell>
                                                                        <Table.HeaderCell width={2}>Tip</Table.HeaderCell>
                                                                        <Table.HeaderCell width={2}>Prevodi se</Table.HeaderCell>
                                                                        <Table.HeaderCell width={1} textAlign="center" verticalAlign="middle">
                                                                            <Popup
                                                                                content="Dodaj atribut"
                                                                                position="top center"
                                                                                trigger={
                                                                                    <Button
                                                                                        type="button"
                                                                                        basic="true"
                                                                                        circular
                                                                                        icon="plus"
                                                                                        size="small"
                                                                                        color="green"
                                                                                        disabled={actionLoading || availableAttributes.length === 0}
                                                                                        onClick={() => this._toggleTemplateAddSection(templateId)}
                                                                                    />
                                                                                }
                                                                            />
                                                                        </Table.HeaderCell>
                                                                    </Table.Row>
                                                                </Table.Header>
                                                                <Table.Body>
                                                                    {sortedAttributeMappings.map(mapping => {
                                                                        const attribute = mapping.attribute || {};
                                                                        return (
                                                                            <Table.Row key={mapping.mappingId}>
                                                                                <Table.Cell verticalAlign="middle">{attribute.id || '-'}</Table.Cell>
                                                                                <Table.Cell verticalAlign="middle" title={attribute.displayName || '-'}>
                                                                                    {attribute.displayName || '-'}
                                                                                </Table.Cell>
                                                                                <Table.Cell verticalAlign="middle" title={attribute.name || '-'}>
                                                                                    {attribute.name || '-'}
                                                                                </Table.Cell>
                                                                                <Table.Cell verticalAlign="middle">{attribute.attributeType || 'TEXT'}</Table.Cell>
                                                                                <Table.Cell verticalAlign="middle">{attribute.translatable === false ? 'Ne' : 'Da'}</Table.Cell>
                                                                                <Table.Cell textAlign="center" verticalAlign="middle">
                                                                                    <Popup
                                                                                        content="Brisanje"
                                                                                        position="top center"
                                                                                        trigger={
                                                                                            <Button
                                                                                                type="button"
                                                                                                basic="true"
                                                                                                circular
                                                                                                icon="trash alternate"
                                                                                                size="small"
                                                                                                color="red"
                                                                                                disabled={actionLoading}
                                                                                                onClick={() => this._handleDeleteMapping(mapping.mappingId)}
                                                                                            />
                                                                                        }
                                                                                    />
                                                                                </Table.Cell>
                                                                            </Table.Row>
                                                                        );
                                                                    })}
                                                                </Table.Body>
                                                            </Table>
                                                        )}
                                                    </div>
                                                </Accordion.Content>
                                            </React.Fragment>
                                        );
                                    })}
                                </Accordion>
                            )}
                        </div>
                    )}
                </div>
                <ToastContainer hideProgressBar />
            </div>
        );
    }
}




// WEBPACK FOOTER //
// ./src/views/DocumentTemplateConfiguration/TemplateCatalogOverview.js